import { Crown, Castle, Shield, User as UserIcon, Medal, BookOpen, Sword, Star, Target, Gem, Heart, Trophy } from 'lucide-react';
import React from 'react';

import { toast } from 'sonner';

export function checkAndToastGamificationUnlock(userId: string, categoryId: string, newValue: number) {
  if (!userId) return;

  const storageKey = `gamification_unlocks_${userId}`;
  let unlockedLevels: Record<string, string> = {};
  
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) unlockedLevels = JSON.parse(stored);
  } catch (e) {
    console.error("Error parsing gamification storage", e);
  }

  // Get the categories
  const categories = getGamificationBadges(
    categoryId === 'favorites' ? newValue : 0,
    categoryId === 'analyses' ? newValue : 0,
    categoryId === 'transactions' ? newValue : 0,
    categoryId === 'loyalty' ? newValue : 0
  );

  const category = categories.find(c => c.id === categoryId);
  if (!category) return;

  const { currentLevel } = computeBadgeProgress(category);

  // If this is the first time we check, just save it without toasting
  // UNLESS they just hit a threshold. Wait, it's better to store and if the level changed, toast.
  if (!unlockedLevels[categoryId]) {
    unlockedLevels[categoryId] = currentLevel.id;
    localStorage.setItem(storageKey, JSON.stringify(unlockedLevels));
    return;
  }

  if (unlockedLevels[categoryId] !== currentLevel.id) {
    const oldLevelIndex = category.levels.findIndex(l => l.id === unlockedLevels[categoryId]);
    const newLevelIndex = category.levels.findIndex(l => l.id === currentLevel.id);
    
    // Only toast if leveled UP
    if (newLevelIndex > oldLevelIndex) {
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-bold text-sm">Nova Conquista Desbloqueada! 🏆</span>
          <span className="text-xs opacity-90">{category.title}: {currentLevel.name}</span>
        </div>,
        { duration: 5000 }
      );
    }
    // Level changed! (Assuming it went up because values only go up, mostly)
    
    toast.success(
      <div className="flex flex-col gap-1">
        <span className="font-bold text-sm">Nova Conquista Desbloqueada! 🏆</span>
        <span className="text-xs opacity-90">{category.title}: {currentLevel.name}</span>
      </div>,
      { duration: 5000 }
    );
    
    // Play a small sound if possible, or just visual feedback.
    unlockedLevels[categoryId] = currentLevel.id;
    localStorage.setItem(storageKey, JSON.stringify(unlockedLevels));
  }
}


export interface BadgeLevel {
  id: string;
  name: string;
  threshold: number;
  icon: React.ElementType;
}

export interface BadgeCategory {
  id: string;
  title: string;
  description: string;
  currentValue: number;
  color: string;
  bgLight: string;
  bgDark: string;
  gradientFrom: string;
  gradientTo: string;
  levels: BadgeLevel[];
}

export function getGamificationBadges(
  favoritesCount: number,
  analysesCount: number,
  transactionsCount: number,
  accountDays: number
): BadgeCategory[] {
  return [
    {
      id: 'favorites',
      title: 'Expansão do Império',
      description: 'Ativos favoritados',
      currentValue: favoritesCount,
      color: 'text-indigo-600 dark:text-indigo-400',
      bgLight: 'bg-indigo-100',
      bgDark: 'dark:bg-indigo-900/50',
      gradientFrom: 'from-indigo-50 dark:from-indigo-900/20',
      gradientTo: 'to-purple-50 dark:to-purple-900/20',
      levels: [
        { id: 'fav_1', name: 'Aldeão', threshold: 0, icon: UserIcon },
        { id: 'fav_2', name: 'Cavaleiro', threshold: 2, icon: Shield },
        { id: 'fav_3', name: 'Construtor Mestre', threshold: 5, icon: Castle },
        { id: 'fav_4', name: 'Lorde do Reino', threshold: 10, icon: Crown },
        { id: 'fav_5', name: 'Imperador', threshold: 25, icon: Gem }
      ]
    },
    {
      id: 'analyses',
      title: 'Sabedoria Real',
      description: 'Análises fundamentalistas',
      currentValue: analysesCount,
      color: 'text-amber-600 dark:text-amber-400',
      bgLight: 'bg-amber-100',
      bgDark: 'dark:bg-amber-900/50',
      gradientFrom: 'from-amber-50 dark:from-amber-900/20',
      gradientTo: 'to-orange-50 dark:to-orange-900/20',
      levels: [
        { id: 'ana_1', name: 'Escudeiro', threshold: 0, icon: Sword },
        { id: 'ana_2', name: 'Analista Real', threshold: 3, icon: BookOpen },
        { id: 'ana_3', name: 'Grão-Vizir', threshold: 10, icon: Medal },
        { id: 'ana_4', name: 'Rei dos Insights', threshold: 25, icon: Crown },
        { id: 'ana_5', name: 'Oráculo Divino', threshold: 50, icon: Star }
      ]
    },
    {
      id: 'transactions',
      title: 'Tesouro Real',
      description: 'Transações no portfólio',
      currentValue: transactionsCount,
      color: 'text-emerald-600 dark:text-emerald-400',
      bgLight: 'bg-emerald-100',
      bgDark: 'dark:bg-emerald-900/50',
      gradientFrom: 'from-emerald-50 dark:from-emerald-900/20',
      gradientTo: 'to-teal-50 dark:to-teal-900/20',
      levels: [
        { id: 'tx_1', name: 'Plebeu', threshold: 0, icon: UserIcon },
        { id: 'tx_2', name: 'Comerciante', threshold: 3, icon: Sword },
        { id: 'tx_3', name: 'Tesoureiro Real', threshold: 10, icon: Medal },
        { id: 'tx_4', name: 'Mestre da Moeda', threshold: 20, icon: Crown },
        { id: 'tx_5', name: 'Dragão Guardião', threshold: 50, icon: Gem }
      ]
    },
    {
      id: 'loyalty',
      title: 'Tempo de Dinastia',
      description: 'Dias de reinado',
      currentValue: accountDays,
      color: 'text-rose-600 dark:text-rose-400',
      bgLight: 'bg-rose-100',
      bgDark: 'dark:bg-rose-900/50',
      gradientFrom: 'from-rose-50 dark:from-rose-900/20',
      gradientTo: 'to-pink-50 dark:to-pink-900/20',
      levels: [
        { id: 'day_1', name: 'Recém Chegado', threshold: 0, icon: Target },
        { id: 'day_2', name: 'Habitante do Reino', threshold: 7, icon: Heart },
        { id: 'day_3', name: 'Nobre Residente', threshold: 30, icon: Shield },
        { id: 'day_4', name: 'Ancião da Corte', threshold: 180, icon: Castle },
        { id: 'day_5', name: 'Lenda Viva', threshold: 365, icon: Trophy }
      ]
    }
  ];
}

export function computeBadgeProgress(category: BadgeCategory) {
  let currentLevel = category.levels[0];
  let nextLevel: BadgeLevel | null = null;
  
  for (let i = 0; i < category.levels.length; i++) {
    if (category.currentValue >= category.levels[i].threshold) {
      currentLevel = category.levels[i];
      nextLevel = category.levels[i + 1] || null;
    } else {
      break;
    }
  }

  let progressPercent = 100;
  let remaining = 0;
  
  if (nextLevel) {
    const currentThreshold = currentLevel.threshold;
    const nextThreshold = nextLevel.threshold;
    const progress = category.currentValue - currentThreshold;
    const total = nextThreshold - currentThreshold;
    progressPercent = Math.min(100, Math.max(0, (progress / total) * 100));
    remaining = nextThreshold - category.currentValue;
  }

  return {
    currentLevel,
    nextLevel,
    progressPercent,
    remaining
  };
}
